import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { 
  ClipboardCheck, 
  Package, 
  FileText, 
  Truck,
  CheckCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  BarChart3,
  Eye
} from 'lucide-react';
import { formatQuantidadeComUnidade } from '../utils/numberFormat';

const parseAnyDate = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  if (typeof raw?.toDate === 'function') return raw.toDate();
  if (raw?._seconds != null) return new Date(raw._seconds * 1000);
  if (raw?.seconds != null) return new Date(raw.seconds * 1000);
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split('-').map((n) => parseInt(n, 10));
    return new Date(y, m - 1, d, 12, 0, 0);
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDataRecebimentoDashboard = (receiving) => {
  const date = parseAnyDate(receiving?.data_recebimento ?? receiving?.criado_em);
  if (!date) return 'N/A';
  return date.toLocaleDateString('pt-BR');
};

const RecebedorDashboard = () => {
  const { user, userData, companyId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingReceivings, setPendingReceivings] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);

  // Função auxiliar para extrair mensagem de erro específica
  const getErrorMessage = (error) => {
    console.log('🔍 Analisando erro:', error);
    
    // Verificar se é um erro do Firebase Functions
    if (error.code && error.code.startsWith('functions/')) {
      return error.message || 'Erro na validação dos dados';
    }
    
    // Verificar códigos específicos
    if (error.code === 'functions/invalid-argument') {
      return error.message || 'Dados inválidos fornecidos';
    }
    
    if (error.code === 'functions/permission-denied') {
      return 'Você não tem permissão para acessar estes dados';
    }
    
    if (error.code === 'functions/not-found') {
      return 'Dados não encontrados para esta empresa';
    }
    
    if (error.code === 'functions/unauthenticated') {
      return 'Sessão expirada. Faça login novamente';
    }
    
    // Verificar se há uma mensagem específica no erro
    if (error.message && error.message !== 'INTERNAL') {
      return error.message;
    }
    
    // Fallback para erro genérico
    return 'Erro ao conectar com o servidor';
  };

  useEffect(() => {
    if (companyId && companyId !== 'default') {
      loadDashboardData();
    }
  }, [companyId]);

  // ✅ NOVO: Recarregar quando voltar para o dashboard (após dar destino final)
  useEffect(() => {
    // Recarregar quando a rota mudar (usuário voltou para o dashboard)
    if (companyId && companyId !== 'default') {
      loadDashboardData();
    }
  }, [location.pathname, companyId]);

  // ✅ REATIVADO: Recarregar dashboard quando recebimento for atualizado
  useEffect(() => {
    const handleRecebimentoAtualizado = () => {
      console.log('🔄 Dashboard: Evento recebimentoAtualizado recebido, recarregando dados...');
      if (companyId && companyId !== 'default') {
        loadDashboardData();
      }
    };
    
    // ✅ NOVO: Recarregar quando a janela receber foco (usuário voltou para a aba)
    const handleFocus = () => {
      console.log('🔄 Dashboard: Janela recebeu foco, recarregando dados...');
      if (companyId && companyId !== 'default') {
        loadDashboardData();
      }
    };
    
    window.addEventListener('recebimentoAtualizado', handleRecebimentoAtualizado);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('recebimentoAtualizado', handleRecebimentoAtualizado);
      window.removeEventListener('focus', handleFocus);
    };
  }, [companyId]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (companyId && companyId !== 'default') {
        try {
          // Tentar carregar estatísticas de recebimentos
          const getReceivingStats = httpsCallable(functions, 'getReceivingStats');
          const statsResult = await getReceivingStats({ empresa_id: companyId });
          console.log('📊 Dashboard: Estatísticas carregadas:', statsResult.data.data);
          
          // Converter dados da API para formato esperado pelo dashboard
          const apiStats = statsResult.data.data;
          const dashboardStats = {
            recebimentos_mes: apiStats.total || 0,
            recebimentos_pendentes: apiStats.por_status?.iniciado || 0,
            recebimentos_aprovados: apiStats.por_decisao?.aceitar || 0,
            recebimentos_rejeitados: apiStats.por_decisao?.rejeitar || 0,
            taxa_aprovacao: apiStats.por_decisao?.aceitar > 0 ? 
              Math.round((apiStats.por_decisao.aceitar / (apiStats.por_decisao.aceitar + apiStats.por_decisao.rejeitar)) * 100) : 0,
            tempo_medio_validacao: 2.5, // Mock - não calculado no backend ainda
            fornecedores_ativos: Object.keys(apiStats.por_fornecedor || {}).length,
            produtos_validados: Object.keys(apiStats.por_produto || {}).length
          };
          console.log('📊 Dashboard: Estatísticas convertidas:', dashboardStats);
          setStats(dashboardStats);

          // Tentar carregar recebimentos pendentes
          const listReceivings = httpsCallable(functions, 'listReceivings');
          const receivingsResult = await listReceivings({ 
            page: 1,
            limit: 1000, // ✅ CORREÇÃO: Aumentar limite para pegar todos os recebimentos
            empresa_id: companyId
          });
          const recebimentosCarregados = receivingsResult.data.data.recebimentos || [];
          console.log('📊 Dashboard: Recebimentos carregados:', recebimentosCarregados.length);
          console.log('📊 Dashboard: Decisões finais dos recebimentos:', recebimentosCarregados.map(r => ({
            id: r.id?.substring(0, 8) + '...',
            produto: r.produto_nome,
            decisao_final: r.decisao_final,
            decisao_qualidade: r.decisao_qualidade?.decisao,
            decisao_final_recebedor: r.decisao_final_recebedor?.decisao
          })));
          setPendingReceivings(recebimentosCarregados);

          // Usar recebimentos recentes como atividades (já que getRecentActivities não existe)
          const recentReceivings = receivingsResult.data.data.recebimentos || [];
          const activities = recentReceivings.slice(0, 5).map(recebimento => ({
            id: recebimento.id,
            tipo: 'recebimento',
            descricao: `Recebimento de ${recebimento.produto_nome} - ${recebimento.fornecedor}`,
            data: recebimento.criado_em,
            status: recebimento.decisao_final || 'em_analise',
            // ✅ CORREÇÃO: Usar recebidoPor.nome se disponível, caso contrário usar criado_por_nome ou fallback
            usuario: recebimento.recebidoPor?.nome || 
                     recebimento.criado_por_nome || 
                     recebimento.usuario_nome || 
                     recebimento.nome_usuario ||
                     'Usuário'
          }));
          console.log('📊 Dashboard: Atividades criadas:', activities);
          setRecentActivities(activities);
        } catch (functionError) {
          console.error('❌ Erro ao carregar dados do dashboard:', functionError);
          // Usar dados vazios se as functions não existirem
          setStats({
            recebimentos_mes: 0,
            recebimentos_pendentes: 0,
            recebimentos_aprovados: 0,
            recebimentos_rejeitados: 0,
            taxa_aprovacao: 0,
            tempo_medio_validacao: 0,
            fornecedores_ativos: 0,
            produtos_validados: 0
          });
          setPendingReceivings([]);
          setRecentActivities([]);
        }
      }

    } catch (error) {
      console.error('❌ Erro geral ao carregar dados do dashboard:', error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const mockStats = {
    recebimentos_mes: 45,
    recebimentos_pendentes: 12,
    recebimentos_aprovados: 38,
    recebimentos_rejeitados: 3,
    taxa_aprovacao: 92.7,
    tempo_medio_validacao: 2.5,
    fornecedores_ativos: 15,
    produtos_validados: 89
  };

  const mockReceivings = [
    {
      id: 1,
      numero_nota_fiscal: 'NF-12345',
      fornecedor: { nome: 'Distribuidora ABC' },
      data_recebimento: new Date(Date.now() - 1000 * 60 * 60 * 2),
      itens: [
        { produto: { nome: 'Açúcar Cristal' }, quantidade_recebida: 50 },
        { produto: { nome: 'Farinha de Trigo' }, quantidade_recebida: 25 }
      ],
      prioridade: 'alta'
    },
    {
      id: 2,
      numero_nota_fiscal: 'NF-12346',
      fornecedor: { nome: 'Alimentos Premium' },
      data_recebimento: new Date(Date.now() - 1000 * 60 * 60 * 4),
      itens: [
        { produto: { nome: 'Óleo de Soja' }, quantidade_recebida: 30 }
      ],
      prioridade: 'media'
    },
    {
      id: 3,
      numero_nota_fiscal: 'NF-12347',
      fornecedor: { nome: 'Fornecedor XYZ' },
      data_recebimento: new Date(Date.now() - 1000 * 60 * 60 * 6),
      itens: [
        { produto: { nome: 'Sal Refinado' }, quantidade_recebida: 100 },
        { produto: { nome: 'Temperos Diversos' }, quantidade_recebida: 20 }
      ],
      prioridade: 'baixa'
    },
    {
      id: 4,
      numero_nota_fiscal: 'NF-12348',
      fornecedor: { nome: 'Indústria Alimentícia' },
      data_recebimento: new Date(Date.now() - 1000 * 60 * 60 * 8),
      itens: [
        { produto: { nome: 'Conservantes Naturais' }, quantidade_recebida: 15 },
        { produto: { nome: 'Embalagens Biodegradáveis' }, quantidade_recebida: 200 }
      ],
      prioridade: 'alta'
    }
  ];

  const mockActivities = [
    {
      id: 1,
      tipo: 'recebimento_aprovado',
      descricao: 'Recebimento NF-12340 aprovado com nota 9.5',
      data: new Date(Date.now() - 1000 * 60 * 30),
      usuario: 'recebedor@empresa.com'
    },
    {
      id: 2,
      tipo: 'validacao_qualidade',
      descricao: 'Qualidade validada para Açúcar Cristal - Lote AC001',
      data: new Date(Date.now() - 1000 * 60 * 60),
      usuario: 'recebedor@empresa.com'
    },
    {
      id: 3,
      tipo: 'recebimento_rejeitado',
      descricao: 'Recebimento NF-12341 rejeitado - embalagem danificada',
      data: new Date(Date.now() - 1000 * 60 * 60 * 2),
      usuario: 'recebedor@empresa.com'
    },
    {
      id: 4,
      tipo: 'recebimento_aprovado',
      descricao: 'Recebimento NF-12342 aprovado - conformidade total',
      data: new Date(Date.now() - 1000 * 60 * 60 * 3),
      usuario: 'recebedor@empresa.com'
    },
    {
      id: 5,
      tipo: 'validacao_qualidade',
      descricao: 'Análise microbiológica aprovada - Farinha Especial',
      data: new Date(Date.now() - 1000 * 60 * 60 * 4),
      usuario: 'recebedor@empresa.com'
    }
  ];

  // ✅ CORRIGIDO: Usar sempre dados da API
  const currentStats = stats || {
    recebimentos_mes: 0,
    recebimentos_pendentes: 0,
    recebimentos_aprovados: 0,
    recebimentos_rejeitados: 0,
    taxa_aprovacao: 0,
    tempo_medio_validacao: 0,
    fornecedores_ativos: 0,
    produtos_validados: 0
  };
  
  // Calcular estatísticas do mês corrente usando useMemo para recalcular apenas quando pendingReceivings mudar
  const statsMesAtual = useMemo(() => {
    const hoje = new Date();
    const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    
    console.log('📊 Calculando estatísticas do mês:', {
      inicioMes,
      totalRecebimentos: pendingReceivings.length,
      recebimentos: pendingReceivings.map(r => ({
        id: r.id,
        criado_em: r.criado_em,
        criado_em_type: typeof r.criado_em,
        criado_em_keys: r.criado_em ? Object.keys(r.criado_em) : [],
        decisao_final: r.decisao_final,
        status: r.status
      }))
    });
    
    const recebimentosDoMes = pendingReceivings.filter(rec => {
      try {
        let dataRecebimento;
        
        // Verificar se é um objeto com _seconds (formato Firebase Functions)
        if (rec.criado_em?._seconds) {
          dataRecebimento = new Date(rec.criado_em._seconds * 1000);
        } else if (rec.criado_em?.toDate) {
          dataRecebimento = rec.criado_em.toDate();
        } else if (rec.criado_em) {
          dataRecebimento = new Date(rec.criado_em);
        }
        
        const isDoMes = dataRecebimento && !isNaN(dataRecebimento.getTime()) && dataRecebimento >= inicioMes;
        console.log('🔍 Verificando recebimento:', {
          id: rec.id,
          criado_em_raw: rec.criado_em,
          dataRecebimento,
          dataRecebimento_valid: dataRecebimento && !isNaN(dataRecebimento.getTime()),
          inicioMes,
          isDoMes,
          decisao_final: rec.decisao_final,
          status: rec.status
        });
        
        return isDoMes;
      } catch (error) {
        console.error('❌ Erro ao processar recebimento:', error, rec);
        return false;
      }
    });
    
    console.log('📊 Recebimentos do mês filtrados:', recebimentosDoMes.length);
    
    const aprovados = recebimentosDoMes.filter(r => r.decisao_final === 'aceitar').length;
    const rejeitados = recebimentosDoMes.filter(r => r.decisao_final === 'rejeitar').length;
    
    // ✅ CORREÇÃO: Pendentes devem usar a MESMA lógica do filtro currentReceivings
    // ✅ CORREÇÃO: Contar TODOS os pendentes (não apenas do mês), para corresponder à lista
    // Excluir recebimentos que já tiveram destino final dado (aceitar/rejeitar)
    // Excluir também se já tem decisão do recebedor (mesmo que decisao_final ainda não tenha sido atualizada)
    const pendentes = pendingReceivings.filter(r => {
      const decisao = r.decisao_final;
      const decisaoRecebedor = r.decisao_final_recebedor?.decisao;
      
      // Excluir recebimentos que já tiveram destino final dado
      if (decisao === 'aceitar' || decisao === 'rejeitar') {
        return false;
      }
      
      // Excluir se já tem decisão do recebedor
      if (decisaoRecebedor === 'aceitar' || decisaoRecebedor === 'rejeitar') {
        return false;
      }
      
      // Incluir apenas os que precisam de ação do recebedor
      return decisao === 'em_analise_qualidade' ||
             decisao === 'aprovado_qualidade' ||
             decisao === 'rejeitado_qualidade';
    }).length;
    const total = aprovados + rejeitados;
    const taxa = total > 0 ? Math.round((aprovados / total) * 100) : 0;
    
    console.log('📊 Estatísticas calculadas:', { 
      aprovados, 
      rejeitados, 
      pendentes, 
      total, 
      taxa,
      detalhes: recebimentosDoMes.map(r => ({
        id: r.id,
        decisao_final: r.decisao_final,
        status: r.status
      }))
    });
    
    return {
      aprovados,
      rejeitados,
      pendentes,
      taxa_sucesso: taxa
    };
  }, [pendingReceivings]); // ✅ Recalcula apenas quando pendingReceivings mudar
  
  // Filtrar recebimentos pendentes:
  // - Em análise pela qualidade
  // - Aprovados pela qualidade (aguardando decisão final do recebedor)
  // - Rejeitados pela qualidade (aguardando decisão final do recebedor)
  // ✅ CORREÇÃO: Excluir explicitamente recebimentos com destino final já dado (aceitar/rejeitar)
  // ✅ CORREÇÃO: Verificar também decisao_final_recebedor para garantir que não mostramos recebimentos já finalizados
  const currentReceivings = pendingReceivings.filter(r => {
    const decisao = r.decisao_final;
    const decisaoRecebedor = r.decisao_final_recebedor?.decisao;
    
    // ✅ CORREÇÃO: Excluir recebimentos que já tiveram destino final dado pelo recebedor
    if (decisao === 'aceitar' || decisao === 'rejeitar') {
      console.log('🔍 RecebedorDashboard: Excluindo recebimento com destino final:', r.produto_nome, decisao);
      return false;
    }
    
    // ✅ CORREÇÃO: Excluir se já tem decisão do recebedor (mesmo que decisao_final ainda não tenha sido atualizada)
    if (decisaoRecebedor === 'aceitar' || decisaoRecebedor === 'rejeitar') {
      console.log('🔍 RecebedorDashboard: Excluindo recebimento com decisão do recebedor:', r.produto_nome, decisaoRecebedor);
      return false;
    }
    
    // Incluir apenas os que precisam de ação do recebedor
    const deveIncluir = decisao === 'em_analise_qualidade' ||
                        decisao === 'aprovado_qualidade' ||
                        decisao === 'rejeitado_qualidade';
    
    if (deveIncluir) {
      console.log('🔍 RecebedorDashboard: Incluindo recebimento pendente:', r.produto_nome, decisao);
    }
    
    return deveIncluir;
  });
  
  // ✅ DEBUG: Log para verificar o filtro
  console.log('🔍 RecebedorDashboard: Total de recebimentos carregados:', pendingReceivings.length);
  console.log('🔍 RecebedorDashboard: Recebimentos após filtro:', currentReceivings.length);
  console.log('🔍 RecebedorDashboard: Decisões finais dos recebimentos:', pendingReceivings.map(r => ({
    id: r.id?.substring(0, 8) + '...',
    produto: r.produto_nome,
    decisao_final: r.decisao_final,
    decisao_qualidade: r.decisao_qualidade?.decisao,
    decisao_final_recebedor: r.decisao_final_recebedor?.decisao
  })));
  const currentActivities = recentActivities;

  const mainCards = [
    {
      title: 'Recebimentos do Mês',
      value: currentStats.recebimentos_mes,
      subtitle: 'Total de recebimentos',
      icon: ClipboardCheck,
      color: 'bg-blue-500'
    },
    {
      title: 'Aprovados',
      value: statsMesAtual.aprovados,
      subtitle: 'Neste mês',
      icon: CheckCircle,
      color: 'bg-green-500'
    },
    {
      title: 'Rejeitados',
      value: statsMesAtual.rejeitados,
      subtitle: 'Neste mês',
      icon: AlertTriangle,
      color: 'bg-red-500'
    },
    {
      title: 'Pendentes',
      value: statsMesAtual.pendentes,
      subtitle: 'Aguardando decisão',
      icon: Clock,
      color: 'bg-yellow-500'
    },
    {
      title: 'Taxa de Sucesso',
      value: `${statsMesAtual.taxa_sucesso}%`,
      subtitle: 'Aprovação neste mês',
      icon: TrendingUp,
      color: 'bg-purple-500'
    }
  ];

  const getPriorityColor = (prioridade) => {
    switch (prioridade) {
      case 'alta':
        return 'bg-red-100 text-red-800';
      case 'media':
        return 'bg-yellow-100 text-yellow-800';
      case 'baixa':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityLabel = (prioridade) => {
    switch (prioridade) {
      case 'alta':
        return 'Alta';
      case 'media':
        return 'Média';
      case 'baixa':
        return 'Baixa';
      default:
        return 'Normal';
    }
  };

  const formatTimeAgo = (date) => {
    if (!date) return '-';
    
    try {
      let d;
      if (date.toDate) {
        d = date.toDate();
      } else if (date instanceof Date) {
        d = date;
      } else {
        d = new Date(date);
      }
      
      // Verificar se a data é válida
      if (isNaN(d.getTime())) {
        return '-';
      }
      
      const now = new Date();
      const diffInMinutes = Math.floor((now.getTime() - d.getTime()) / (1000 * 60));
      
      if (diffInMinutes < 0) {
        return 'agora';
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes} min atrás`;
      } else if (diffInMinutes < 1440) {
        return `${Math.floor(diffInMinutes / 60)} h atrás`;
      } else {
        return `${Math.floor(diffInMinutes / 1440)} dias atrás`;
      }
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return '-';
    }
  };

  const getActivityIcon = (tipo) => {
    switch (tipo) {
      case 'recebimento_aprovado':
        return CheckCircle;
      case 'recebimento_rejeitado':
        return AlertTriangle;
      case 'validacao_qualidade':
        return ClipboardCheck;
      default:
        return FileText;
    }
  };

  const getActivityColor = (tipo) => {
    switch (tipo) {
      case 'recebimento_aprovado':
        return 'text-green-600 bg-green-100';
      case 'recebimento_rejeitado':
        return 'text-red-600 bg-red-100';
      case 'validacao_qualidade':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2" />
          {error}
          <button 
            onClick={loadDashboardData}
            className="ml-auto text-red-700 hover:text-red-900 underline text-sm"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          Bem-vindo, {userData?.nome || user?.displayName || 'Recebedor'}
        </h1>
        <p className="text-blue-100">
          Central de controle de qualidade - Validação de recebimentos e produtos
        </p>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {mainCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{card.value}</p>
                  <p className="text-sm text-gray-500 mt-1">{card.subtitle}</p>
                </div>
                <div className={`${card.color} p-3 rounded-lg`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
              </div>
              {card.trend && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-green-600 font-medium">{card.trend}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pending Receivings and Activities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Receivings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <ClipboardCheck className="h-5 w-5 mr-2" />
                Recebimentos Pendentes
              </h3>
              <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                {currentReceivings.length} pendentes
              </span>
            </div>
          </div>
          <div className="p-6">
            {currentReceivings.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">Nenhum recebimento pendente</p>
              </div>
            ) : (
              <div className="space-y-4">
                {currentReceivings.map((receiving) => {
                  const getStatusBadge = () => {
                    switch(receiving.decisao_final) {
                      case 'em_analise_qualidade':
                        return <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded-full">Em Análise - Qualidade</span>;
                      case 'aprovado_qualidade':
                        return <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">✓ Aprovado - Qualidade</span>;
                      case 'rejeitado_qualidade':
                        return <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">✗ Rejeitado - Qualidade</span>;
                      default:
                        return <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded-full">Pendente</span>;
                    }
                  };

                  return (
                    <div key={receiving.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{receiving.produto_nome}</p>
                          <p className="text-xs text-gray-600 mt-1">
                            {receiving.fornecedor} | Lote: {receiving.lote_fornecedor}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Data: {formatDataRecebimentoDashboard(receiving)}
                            {' · '}
                            NF: {receiving.numero_nota_fiscal || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatQuantidadeComUnidade(receiving.quantidade_recebida, receiving.unidade_medida)}
                          </p>
                        </div>
                        {getStatusBadge()}
                      </div>
                      
                      {/* Observações da Qualidade */}
                      {receiving.decisao_qualidade?.observacoes && (
                        <div className="mt-3 p-3 bg-purple-50 rounded border-l-4 border-purple-500">
                          <p className="text-xs font-medium text-purple-900">Observações da Qualidade:</p>
                          <p className="text-xs text-purple-700 mt-1">{receiving.decisao_qualidade.observacoes}</p>
                        </div>
                      )}

                      {/* Botão de ação baseado no status */}
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        {receiving.decisao_final === 'em_analise_qualidade' ? (
                          <div className="text-sm text-gray-500 italic">
                            ⏳ Aguardando análise da Qualidade...
                          </div>
                        ) : (
                          <button 
                            onClick={() => navigate('/app/receivings', { state: { receivingId: receiving.id } })}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            {receiving.decisao_final === 'aprovado_qualidade' ? 'Dar Destino Final →' : 'Ver Detalhes →'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Recent Activities */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              Atividades Recentes
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {currentActivities.map((activity) => {
                const Icon = getActivityIcon(activity.tipo);
                return (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className={`p-2 rounded-full ${getActivityColor(activity.tipo)}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{activity.descricao}</p>
                      {activity.usuario && activity.usuario !== 'Usuário' ? (
                        <p className="text-sm text-gray-500">por {activity.usuario}</p>
                      ) : (
                        <p className="text-sm text-gray-400 italic">por usuário do sistema</p>
                      )}
                      <p className="text-xs text-gray-400">{formatTimeAgo(activity.data)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default RecebedorDashboard;

